import { API_BASE_URL } from "../config";

export type ApiKey = {
  api_key_id: string;
  name: string;
  created_at: string;
  expires_at: string | null;
};

export type ApiKeyCreateResponse = ApiKey & {
  api_key_token: string;
};

export const fetchApiKeys = async (accessToken: string): Promise<ApiKey[]> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/api-keys`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (res.status === 403) {
    throw new Error("You don't have permission to manage API keys. Contact your administrator to request access.");
  }
  if (!res.ok) {
    throw new Error("Unable to load API keys. Please try again later.");
  }

  return res.json();
};

export const createApiKey = async (
  accessToken: string,
  data: { name: string; expires_in?: number },
): Promise<ApiKeyCreateResponse> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/api-keys`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.detail || "Unable to create API key. Please try again later.");
  }

  return res.json();
};

export const updateApiKey = async (
  accessToken: string,
  apiKeyId: string,
  data: { name: string },
): Promise<ApiKey> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/api-keys/${apiKeyId}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.detail || "Unable to update API key. Please try again later.");
  }

  return res.json();
};

export const deleteApiKey = async (
  accessToken: string,
  apiKeyId: string,
): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/v1/api-keys/${apiKeyId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.detail || "Unable to delete API key. Please try again later.");
  }
};
